import { useEffect, useRef } from "react";
import { api, API_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAX_BACKOFF = 15000;

export default function RealtimeBridge() {
  const { status, user } = useAuth();
  const sourceRef = useRef(null);
  const reconnectRef = useRef(null);
  const backoffRef = useRef(1000);
  const dispatchRef = useRef(null);
  const pendingRef = useRef([]);

  useEffect(() => {
    if (status !== "authed" || !user || user.role === "client" || user.role === "platform_admin") return undefined;
    let cancelled = false;
    let connecting = false;

    const close = () => {
      if (sourceRef.current) {
        sourceRef.current.close();
        sourceRef.current = null;
      }
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current);
        reconnectRef.current = null;
      }
    };

    const scheduleDispatch = (detail) => {
      pendingRef.current.push(detail);
      // A fixed coalescing window must still flush under continuous activity.
      if (dispatchRef.current) return;
      dispatchRef.current = setTimeout(() => {
        const events = pendingRef.current.splice(0);
        const eventTypes = [...new Set(events.map((item) => item?.event_type).filter(Boolean))];
        const entityTypes = [...new Set(events.map((item) => item?.entity_type).filter(Boolean))];
        const merged = {
          source: "realtime",
          events,
          event_types: eventTypes,
          entity_types: entityTypes,
          latest: events[events.length - 1] || null,
        };
        window.dispatchEvent(new CustomEvent("tps:realtime", { detail: merged }));
        window.dispatchEvent(new CustomEvent("tps:data-changed", { detail: merged }));
        if (eventTypes.some((type) => type && !type.startsWith("presence."))) {
          window.dispatchEvent(new Event("tps:notifications-changed"));
        }
        dispatchRef.current = null;
      }, 90);
    };

    const connect = async () => {
      if (cancelled || connecting || sourceRef.current) return;
      connecting = true;
      reconnectRef.current = null;
      try {
        const auth = await api.get("/realtime/ticket");
        if (cancelled || !auth?.ticket) return;
        const source = new EventSource(`${API_URL}/realtime/stream?ticket=${encodeURIComponent(auth.ticket)}`);
        sourceRef.current = source;
        source.addEventListener("ready", () => {
          backoffRef.current = 1000;
          // Reconnect is a catch-up boundary: rebuild mounted projections from
          // canonical state instead of assuming no events were missed offline.
          window.dispatchEvent(new CustomEvent("tps:data-changed", {
            detail: { source: "realtime-ready", resync: true },
          }));
        });
        source.addEventListener("change", (event) => {
          try { scheduleDispatch(JSON.parse(event.data)); } catch {}
        });
        source.onerror = () => {
          source.close();
          if (sourceRef.current !== source) return;
          sourceRef.current = null;
          if (cancelled) return;
          const wait = backoffRef.current;
          backoffRef.current = Math.min(MAX_BACKOFF, Math.round(wait * 1.7));
          reconnectRef.current = setTimeout(connect, wait);
        };
      } catch {
        if (cancelled) return;
        const wait = backoffRef.current;
        backoffRef.current = Math.min(MAX_BACKOFF, Math.round(wait * 1.7));
        reconnectRef.current = setTimeout(connect, wait);
      } finally {
        connecting = false;
      }
    };

    connect();
    const visible = () => {
      if (document.visibilityState === "visible" && !sourceRef.current && !reconnectRef.current) connect();
    };
    document.addEventListener("visibilitychange", visible);
    window.addEventListener("online", visible);
    return () => {
      cancelled = true;
      close();
      if (dispatchRef.current) clearTimeout(dispatchRef.current);
      dispatchRef.current = null;
      pendingRef.current = [];
      document.removeEventListener("visibilitychange", visible);
      window.removeEventListener("online", visible);
    };
  }, [status, user?.id, user?.role, user?.studio_id]);

  return null;
}
